import { type ForwardedRef, forwardRef, type Key, type ReactNode, useEffect, useMemo, useRef } from 'react';
import type { GridListProps } from 'react-aria-components';
import { DropIndicator, GridList, GridListItem, useDragAndDrop } from 'react-aria-components';
import type { ProjectContext } from '@gonfalon/app-context/projectContext';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { isSandbox } from '@gonfalon/constants/constants';
import { useScrollIntoViewOnMount } from '@gonfalon/dom/useScrollIntoViewOnMount';
import { useTextOverflow } from '@gonfalon/dom/useTextOverflow';
import { EnvironmentMarker } from '@gonfalon/environments/EnvironmentMarker';
import { MarketplaceEnvironmentIndicator } from '@gonfalon/environments/MarketplaceEnvironmentIndicator/MarketplaceEnvironmentIndicator';
import type { InternalEnvironment } from '@gonfalon/environments/types';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useThemeValue } from '@gonfalon/theme/useThemeValue';
import { IconButton, MenuTrigger, Popover, Tooltip, TooltipTrigger, useHover } from '@launchpad-ui/components';
import clsx from 'clsx';
import nullthrows from 'nullthrows';
import { z } from 'zod';

import { trackEnvironmentEvent } from '../analytics';
import { EnvironmentFilters } from '../EnvironmentFilters';
import { type SelectedEnvironment } from '../types';
import { useEventLocation } from '../utils/getEventLocation';

import { computeRingColor } from './computeRingColor';
import { EnvironmentActionsMenu } from './EnvironmentActionsMenu';
import { ReplaceEnvironmentMenuTrigger } from './ReplaceEnvironmentMenuTrigger';

import styles from './EnvironmentSelector.module.css';

const mimetype = 'custom/launchdarkly';
const dragItemDataSchema = z.object({ id: z.string(), name: z.string(), color: z.string() });

function parseDragItemData(data: string) {
  return dragItemDataSchema.parse(JSON.parse(data));
}

type EnvironmentSelectorProps = GridListProps<SelectedEnvironment> & {
  projectKey: string;
  addButton?: ReactNode;
  excludedIds: Set<string>;
  onReorder: (environmentKey: Key, position: 'before' | 'after', otherKeys: Set<Key>) => void;
  onRemove: (environmentKey: Key) => void;
  onReplace: (sourceEnvironment: Key, targetEnvironment: InternalEnvironment) => void;
};

export function EnvironmentSelector({
  projectKey,
  children,
  selectionMode = 'single',
  addButton,
  excludedIds,
  onReorder,
  onRemove,
  onReplace,
  ...props
}: EnvironmentSelectorProps) {
  const { context } = useProjectContext();
  const list = Array.from(props.items || []);

  const { dragAndDropHooks } = useDragAndDrop({
    acceptedDragTypes: ['text/plain', mimetype],
    getItems(keys) {
      return [...keys].map((key) => {
        const item = nullthrows(list.find((env) => env.key === key));
        return {
          'text/plain': item.key,
          [mimetype]: JSON.stringify({ id: item._id, name: item.name, color: item.color }),
        };
      });
    },
    onReorder(event) {
      if (event.target.dropPosition === 'on') {
        return;
      }

      onReorder(event.target.key, event.target.dropPosition, event.keys);
    },
    renderDropIndicator(target) {
      return <DropIndicator target={target} className={styles.dropIndicator} />;
    },
    renderDragPreview([item]) {
      const data = parseDragItemData(item[mimetype]);
      return (
        <div className={styles.dragPreview}>
          <EnvironmentMarker color={data.color} />
          {data.name}
        </div>
      );
    },
  });

  const { ref } = useScrollIntoViewOnMount<HTMLDivElement>();
  const selectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selector = selectorRef.current;
    if (!selector) {
      return;
    }

    // The environment selector scrolls independently of the layout's main scroller, so we
    // mirror its scroll state onto the sticky context to drive the controls edge fade.
    const contextEl = selector.closest('[data-context]');

    const syncScrollState = () => {
      const { scrollLeft, clientWidth, scrollWidth } = selector;
      const isScrolled = scrollLeft > 0;
      const isScrolledRight = scrollLeft + clientWidth < scrollWidth - 1;

      selector.toggleAttribute('data-scrolled', isScrolled);
      selector.toggleAttribute('data-scrolled-right', isScrolledRight);

      if (contextEl) {
        contextEl.toggleAttribute('data-scrolled', isScrolled);
        contextEl.toggleAttribute('data-scrolled-right', isScrolledRight);
      }
    };

    selector.addEventListener('scroll', syncScrollState, { passive: true });

    // Whether the selector overflows changes without it ever being scrolled: the tabs have no
    // width yet on the first measurement, and afterwards the space available to them keeps
    // moving as the window is resized, the entity sidebar is dragged or collapsed, and
    // environments are added or removed. Measuring on scroll alone leaves the edge fades stale,
    // which hides the fact that there are more environments past the edge.
    const resizeObserver = new ResizeObserver(syncScrollState);
    resizeObserver.observe(selector);
    for (const child of selector.children) {
      resizeObserver.observe(child);
    }

    syncScrollState();

    return () => {
      selector.removeEventListener('scroll', syncScrollState);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={selectorRef} className={styles.environmentSelector} data-selection-mode={selectionMode}>
      <GridList
        {...props}
        layout="grid"
        className={styles.list}
        aria-label={msg('chrome.environmentSelector.label')}
        dragAndDropHooks={dragAndDropHooks}
        selectionMode={selectionMode === 'single' ? 'single' : 'multiple'}
      >
        {(item) => (
          <GridListItem textValue={item.name} id={item.key} key={item.key} className={styles.item}>
            {({ isSelected }) => (
              <EnvironmentSelectorItem
                ref={ref}
                item={item}
                excludedIds={excludedIds}
                context={context}
                isSelected={isSelected}
                onRemove={list.length >= 2 ? onRemove : undefined}
                onReplace={onReplace}
              />
            )}
          </GridListItem>
        )}
      </GridList>
      {addButton ? <div className={styles.add}>{addButton}</div> : null}
    </div>
  );
}

type EnvironmentSelectorItemProps = {
  context: ProjectContext;
  item: SelectedEnvironment;
  excludedIds: Set<string>;
  isSelected: boolean;
  onRemove?: (environmentKey: Key) => void;
  onReplace: (sourceEnvironment: Key, targetEnvironment: InternalEnvironment) => void;
};

const EnvironmentSelectorItem = forwardRef(
  (
    { item, excludedIds, context, isSelected, onRemove, onReplace }: EnvironmentSelectorItemProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { ref: itemNameRef, hasOverflow, props: overflowProps } = useTextOverflow<HTMLDivElement>();
    const { hoverProps, isHovered } = useHover({});
    const location = useEventLocation();

    const theme = useThemeValue();
    const ringColor = useMemo(() => computeRingColor(`#${item.color}`, theme), [item.color, theme]);

    return (
      <div
        ref={isSelected ? ref : undefined}
        className={styles.environment}
        style={{
          // @ts-expect-error --environment-ring-color is not a known css property
          '--environment-ring-color': ringColor,
        }}
      >
        {/* @ts-expect-error RAC adds an aria label */}
        <IconButton slot="drag" variant="minimal" icon="grip-horiz" size="small" className={styles.drag} />
        <EnvironmentMarker className={styles.marker} color={item.color} />
        <div className={styles.nameGroup}>
          <TooltipTrigger isDisabled={!hasOverflow} isOpen={hasOverflow && isHovered}>
            <div
              ref={itemNameRef}
              data-text={item.name}
              {...overflowProps}
              {...hoverProps}
              className={clsx(styles.name, overflowProps.className)}
            >
              {item.name}
            </div>
            <Tooltip triggerRef={itemNameRef} placement="bottom start" className={styles.overflowName}>
              {item.name}
            </Tooltip>
          </TooltipTrigger>
          <MarketplaceEnvironmentIndicator envKey={item.key} projectKey={context.projectKey} />
        </div>

        <TooltipTrigger>
          <ReplaceEnvironmentMenuTrigger
            className={styles.swap}
            projectKey={context.projectKey}
            environmentName={item.name}
            excludedIds={new Set([item._id, ...Array.from(excludedIds)])}
            onSelect={(env) => {
              onReplace(item.key, env);
            }}
          />
          <Tooltip placement="bottom">
            <Message id="chrome.environmentSelector.swap" />
          </Tooltip>
        </TooltipTrigger>

        <MenuTrigger
          onOpenChange={(open) => {
            if (open) {
              trackEnvironmentEvent('More Environment Actions Clicked', { environmentKey: item.key, location });
            }
          }}
        >
          <TooltipTrigger>
            <IconButton
              aria-label={msg('chrome.environmentSelector.moreActionsFor', { environmentName: item.name })}
              variant="minimal"
              icon="more-vert"
              size="small"
              className={styles.actions}
              isDisabled={isSandbox()}
            />
            <Tooltip placement="bottom">
              <Message id="chrome.environmentSelector.moreActions" />
            </Tooltip>
          </TooltipTrigger>
          <Popover placement="bottom end">
            <EnvironmentActionsMenu environment={item} projectKey={context.projectKey} />
          </Popover>
        </MenuTrigger>
        <EnvironmentFilters environment={item} />
        {onRemove !== undefined ? (
          <TooltipTrigger>
            <IconButton
              aria-label={msg('chrome.environmentSelector.closeNamed', { environmentName: item.name })}
              icon="cancel"
              size="small"
              variant="minimal"
              className={styles.remove}
              onPress={() => {
                trackEnvironmentEvent('Close Environment Clicked', { environmentKey: item.key, location });
                onRemove(item.key);
              }}
            />
            <Tooltip placement="bottom">
              <Message id="chrome.environmentSelector.close" />
            </Tooltip>
          </TooltipTrigger>
        ) : null}
      </div>
    );
  },
);
